// A very small promise wrapper over IndexedDB. Two stores and no queries, so
// a library would be more code than this is.
//
// Every call resolves to a fallback rather than rejecting when storage is
// unavailable: private windows, cleared site data, and browsers configured to
// block it all fail here, and none of them should stop someone writing.

const DB_NAME = "metis";
const DB_VERSION = 1;

export const DOCUMENTS = "documents";
export const OUTBOX = "outbox";

let opening: Promise<IDBDatabase | null> | null = null;

function open(): Promise<IDBDatabase | null> {
  if (opening) return opening;
  opening = new Promise((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DOCUMENTS)) db.createObjectStore(DOCUMENTS);
      if (!db.objectStoreNames.contains(OUTBOX)) {
        const store = db.createObjectStore(OUTBOX, { keyPath: "key" });
        store.createIndex("documentId", "documentId");
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });
  return opening;
}

async function run<T>(
  store: string,
  mode: IDBTransactionMode,
  body: (s: IDBObjectStore) => IDBRequest,
  fallback: T,
): Promise<T> {
  const db = await open();
  if (!db) return fallback;
  return new Promise<T>((resolve) => {
    try {
      const tx = db.transaction(store, mode);
      const request = body(tx.objectStore(store));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(fallback);
      tx.onabort = () => resolve(fallback);
    } catch {
      resolve(fallback);
    }
  });
}

export function get<T>(store: string, key: IDBValidKey): Promise<T | null> {
  return run<T | null>(store, "readonly", (s) => s.get(key), null).then((v) => v ?? null);
}

export function put(store: string, value: unknown, key?: IDBValidKey): Promise<unknown> {
  return run(store, "readwrite", (s) => (key === undefined ? s.put(value) : s.put(value, key)), null);
}

export function remove(store: string, key: IDBValidKey): Promise<unknown> {
  return run(store, "readwrite", (s) => s.delete(key), null);
}

export function allByIndex<T>(store: string, index: string, value: IDBValidKey): Promise<T[]> {
  return run<T[]>(store, "readonly", (s) => s.index(index).getAll(value), []).then((v) => v ?? []);
}

export function removeMany(store: string, keys: IDBValidKey[]): Promise<unknown> {
  if (keys.length === 0) return Promise.resolve(null);
  return run(
    store,
    "readwrite",
    (s) => {
      let last: IDBRequest = s.delete(keys[0]!);
      for (const key of keys.slice(1)) last = s.delete(key);
      return last;
    },
    null,
  );
}

export function count(store: string): Promise<number> {
  return run<number>(store, "readonly", (s) => s.count(), 0).then((v) => v ?? 0);
}

/** Forget everything this device holds for the account that was signed in.
 *  A shared iPad must not open on the last person's library. */
export async function clearAll(): Promise<void> {
  await run(DOCUMENTS, "readwrite", (s) => s.clear(), null);
  await run(OUTBOX, "readwrite", (s) => s.clear(), null);
}
